import {
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Button, SearchBar } from "@legal-500/legal500-design-system";
import {
  type ClashChoice,
  firmKey,
  type ImportItem,
  type ImportPreviewOrg,
  slotsInOrder,
} from "../../utils/bpaImport";
import {
  applyPreset,
  changeCount,
  changedItemIds,
  type FirmControl,
  firmSentence,
  LEFT_OUT_FIRM_REASON,
  replacesExistingItem,
  resetFirm,
  type SwitchPosition,
  switchDisabledReason,
  switchPositions,
  toggleItem,
} from "../../utils/bpaImportItemControl";
import { languageName } from "../../utils/languageName";
import { ColouredWords, Named, type Side } from "./ColouredWords";
import { SegmentedToggle } from "./controls/SegmentedToggle";
import { REPLACE_NEEDS_DELETE_REASON, sarsScoreText } from "./importText";
import { SkeletonRows } from "./SkeletonRows";
import styles from "./ComparePanel.module.css";
import shared from "./importShared.module.css";

type ComparePanelProps = {
  org: ImportPreviewOrg;
  control: FirmControl;
  onChange: (control: FirmControl) => void;
  sourceName: string;
  targetName: string;
  canDelete: boolean;
  loading?: boolean;
  onClose: () => void;
  open?: boolean;
  moving?: boolean;
  shellRef?: Ref<HTMLDivElement>;
  titleRef?: Ref<HTMLHeadingElement>;
};

type SlotRow = {
  slot: string;
  heading: string;
  source: ImportItem[];
  target: ImportItem[];
};

type ChoiceOption = { position: SwitchPosition; label: string; itemId: string };

const SKELETON_ROWS = 3;
const SEARCH_FROM_ITEMS = 10;
const PRESET_HEIGHT_PROPERTY = "--compare-preset-height";

function matchesLawyer(item: ImportItem, search: string): boolean {
  const wanted = search.trim().toLowerCase();
  if (!wanted) return true;
  return item.contactName?.toLowerCase().includes(wanted) ?? false;
}

function rankingList(item: ImportItem, side: Side): string | null {
  return side === "incoming" ? (item.landingTableName ?? item.tableName) : item.tableName;
}

function slotHeading(item: ImportItem, side: Side): string {
  const language = item.languageIso ? languageName(item.languageIso) : null;
  if (item.kind === "commentary") {
    return ["Commentary", item.contactName, language].filter(Boolean).join(" · ");
  }
  if (item.kind === "submission") {
    return ["Submission", item.contactName, language, rankingList(item, side)]
      .filter(Boolean)
      .join(" · ");
  }
  return ["Ranking", item.contactName, rankingList(item, side)].filter(Boolean).join(" · ");
}

function keepSeparatorsOnTheLine(heading: string): string {
  return heading.replaceAll(" · ", "\u00a0· ");
}

function slotRowsOf(org: ImportPreviewOrg): SlotRow[] {
  const entries = [
    ...(org.items?.source ?? []).map(item => ({ item, side: "incoming" as Side })),
    ...(org.items?.target ?? []).map(item => ({ item, side: "existing" as Side })),
  ];
  return slotsInOrder(entries, entry => entry.item).map(members => {
    const source = members.filter(member => member.side === "incoming").map(member => member.item);
    const target = members.filter(member => member.side === "existing").map(member => member.item);
    const heading =
      source.length > 0 ? slotHeading(source[0], "incoming") : slotHeading(target[0], "existing");
    return { slot: members[0].item.slot, heading, source, target };
  });
}

function dayAndMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function submissionMeta(org: ImportPreviewOrg, item: ImportItem, side: Side): string | null {
  const known = org.compare?.[side === "incoming" ? "source" : "target"].submissions;
  const found = known?.find(candidate => candidate.id === item.id);
  if (!found) return null;
  return sarsScoreText(found.sarsOverall);
}

function landingNameOf(item: ImportItem, side: Side, struck: boolean): string | null {
  if (side !== "incoming" || struck) return null;
  if (!item.landingTableName || item.landingTableName === item.tableName) return null;
  return item.landingTableName;
}

function rankingOtherFirmNote(item: ImportItem, side: Side): string | null {
  if (side !== "existing") return null;
  if (item.decidedUnder) return `decided under ${item.decidedUnder}`;
  if (item.heldUnder) return `ranked here under ${item.heldUnder}`;
  return null;
}

function isDecidedElsewhere(row: SlotRow): boolean {
  return row.source.length === 0 && row.target.every(item => Boolean(item.decidedUnder));
}

type ItemBodyProps = {
  org: ImportPreviewOrg;
  item: ImportItem;
  side: Side;
  struck: boolean;
  marks: ReactNode;
};

function ItemBody({ org, item, side, struck, marks }: ItemBodyProps) {
  if (item.kind === "submission") {
    const meta = submissionMeta(org, item, side);
    return (
      <div className={styles.itemText}>
        <div className={styles.value}>
          {item.submittedAt ? `Submitted ${dayAndMonth(item.submittedAt)}` : "Submission"}
          {marks}
        </div>
        {meta && <div className={styles.meta}>{meta}</div>}
      </div>
    );
  }
  if (item.kind === "commentary") {
    return (
      <div className={styles.itemText}>
        <div className={`${styles.value} ${styles.quote}`}>
          “{item.excerpt}”{marks}
        </div>
      </div>
    );
  }
  const landing = landingNameOf(item, side, struck);
  const otherFirmNote = rankingOtherFirmNote(item, side);
  return (
    <div className={styles.itemText}>
      <div className={styles.value}>
        {item.tier === null ? "No tier" : `Tier ${item.tier}`}
        {marks}
      </div>
      {landing && <div className={styles.meta}>→ {landing}</div>}
      {otherFirmNote && <div className={styles.meta}>{otherFirmNote}</div>}
    </div>
  );
}

function wontStay(side: Side, position: SwitchPosition): boolean {
  if (side === "incoming") return position === "leave";
  return position === "replace" || position === "remove";
}

function isMarkedRemoved(side: Side, position: SwitchPosition): boolean {
  return side === "existing" && position === "remove";
}

function choiceOptionsFor(row: SlotRow): ChoiceOption[] {
  const source = row.source[0];
  const target = row.target[0];
  if (source && target) {
    return [
      { position: "bring", label: "Source's", itemId: source.id },
      { position: "keep", label: "Target's", itemId: target.id },
    ];
  }
  if (source) {
    return [
      { position: "bring", label: "Bring", itemId: source.id },
      { position: "leave", label: "Leave", itemId: source.id },
    ];
  }
  return [
    { position: "keep", label: "Keep", itemId: target.id },
    { position: "remove", label: "Remove", itemId: target.id },
  ];
}

function chosenPosition(row: SlotRow, positions: Record<string, SwitchPosition>): SwitchPosition {
  const source = row.source[0];
  const target = row.target[0];
  if (source && target) return positions[source.id] === "bring" ? "bring" : "keep";
  return positions[(source ?? target).id];
}

function SideName({ side, word, name }: { side: Side; word: string; name: string }) {
  return (
    <div className={styles.side} title={name}>
      <Named side={side}>{word}</Named>
      <Named bold side={side}>
        {name}
      </Named>
    </div>
  );
}

type ItemCellProps = {
  org: ImportPreviewOrg;
  items: ImportItem[];
  side: Side;
  positions: Record<string, SwitchPosition>;
  changedIds: string[];
};

function ItemCell({ org, items, side, positions, changedIds }: ItemCellProps) {
  if (items.length === 0) {
    return (
      <div role="cell" className={`${styles.value} ${styles.none}`}>
        None
      </div>
    );
  }
  return (
    <div role="cell">
      <ul className={styles.items}>
        {items.map(item => {
          const changed = changedIds.includes(item.id);
          const removed = isMarkedRemoved(side, positions[item.id]);
          const struck = wontStay(side, positions[item.id]) && !removed;
          return (
            <li key={item.id} className={styles.item} data-struck={struck} data-changed={changed}>
              <ItemBody
                org={org}
                item={item}
                side={side}
                struck={struck}
                marks={
                  <>
                    {struck && <span className={styles.srOnly}>Won't stay</span>}
                    {removed && <span className={styles.removed}>Removed</span>}
                    {changed && !removed && <span className={styles.changed}>Changed</span>}
                  </>
                }
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type SlotRowViewProps = {
  org: ImportPreviewOrg;
  row: SlotRow;
  control: FirmControl;
  positions: Record<string, SwitchPosition>;
  changedIds: string[];
  canDelete: boolean;
  leftOutReason: string | null;
  onChange: (control: FirmControl) => void;
};

function SlotRowView({
  org,
  row,
  control,
  positions,
  changedIds,
  canDelete,
  leftOutReason,
  onChange,
}: SlotRowViewProps) {
  const options = choiceOptionsFor(row);
  const paired = row.source.length > 0 && row.target.length > 0;
  const decidedElsewhere = isDecidedElsewhere(row);
  function pick(position: SwitchPosition) {
    const option = options.find(candidate => candidate.position === position);
    if (option) onChange(toggleItem(org, control, option.itemId, option.position));
  }
  return (
    <div role="row" aria-label={row.heading} className={styles.slotRow}>
      <div role="rowheader" className={styles.slotHead}>
        <h3 className={styles.slotTitle} aria-label={row.heading}>
          {keepSeparatorsOnTheLine(row.heading)}
        </h3>
        {paired && <span className={styles.onlyOne}>Only one can stay</span>}
      </div>
      <ItemCell
        org={org}
        items={row.source}
        side="incoming"
        positions={positions}
        changedIds={changedIds}
      />
      <ItemCell
        org={org}
        items={row.target}
        side="existing"
        positions={positions}
        changedIds={changedIds}
      />
      <div role="cell">
        {!decidedElsewhere && (
          <SegmentedToggle<SwitchPosition>
            size="small"
            fill
            name={`choice-${row.slot}`}
            ariaLabel={`Choice for ${row.heading}`}
            value={chosenPosition(row, positions)}
            options={options.map(option => {
              const reason =
                leftOutReason ??
                switchDisabledReason(org, option.itemId, option.position, canDelete);
              return {
                value: option.position,
                text: option.label,
                disabled: reason !== null,
                title: reason ?? undefined,
              };
            })}
            onChange={pick}
          />
        )}
      </div>
    </div>
  );
}

type ItemTableProps = {
  org: ImportPreviewOrg;
  title: string;
  rows: SlotRow[];
  control: FirmControl;
  canDelete: boolean;
  leftOutReason: string | null;
  onChange: (control: FirmControl) => void;
};

function ItemTable({
  org,
  title,
  rows,
  control,
  canDelete,
  leftOutReason,
  onChange,
}: ItemTableProps) {
  const positions = switchPositions(org, control);
  const changedIds = changedItemIds(org, control);
  return (
    <section className={styles.tableBlock}>
      <div className={styles.tableTitle} aria-hidden="true">
        {title}
      </div>
      <div className={styles.group} role="table" aria-label={title}>
        <div role="row" className={styles.columnHeads}>
          <div role="columnheader">Item</div>
          <div role="columnheader" data-side="incoming">
            Source content
          </div>
          <div role="columnheader" data-side="existing">
            Target content
          </div>
          <div role="columnheader">Choice</div>
        </div>
        {rows.map(row => (
          <SlotRowView
            key={row.slot}
            org={org}
            row={row}
            control={control}
            positions={positions}
            changedIds={changedIds}
            canDelete={canDelete}
            leftOutReason={leftOutReason}
            onChange={onChange}
          />
        ))}
      </div>
    </section>
  );
}

function ItemTables({
  rows,
  search,
  ...tableProps
}: Omit<ItemTableProps, "title" | "rows"> & { rows: SlotRow[]; search: string }) {
  if (rows.length === 0) {
    return (
      <div className={styles.group}>
        <div className={`${styles.value} ${styles.none} ${styles.notice}`}>None</div>
      </div>
    );
  }
  const shown = rows.filter(row => matchesLawyer(row.source[0] ?? row.target[0], search));
  if (shown.length === 0) {
    return (
      <div className={styles.group}>
        <div className={`${styles.value} ${styles.none} ${styles.notice}`}>No lawyer matches</div>
      </div>
    );
  }
  const tables = [
    { title: "In both", rows: shown.filter(row => row.source.length > 0 && row.target.length > 0) },
    { title: "Only in the source", rows: shown.filter(row => row.target.length === 0) },
    { title: "Only in the target", rows: shown.filter(row => row.source.length === 0) },
  ];
  return (
    <div className={styles.tables}>
      {tables
        .filter(table => table.rows.length > 0)
        .map(table => (
          <ItemTable key={table.title} {...tableProps} title={table.title} rows={table.rows} />
        ))}
    </div>
  );
}

function presetReasonFor(org: ImportPreviewOrg, canDelete: boolean): string | null {
  return !canDelete && replacesExistingItem(org) ? REPLACE_NEEDS_DELETE_REASON : null;
}

function switchReasonFor(
  org: ImportPreviewOrg,
  rows: SlotRow[],
  canDelete: boolean,
): string | null {
  const blocked = rows.some(row =>
    choiceOptionsFor(row).some(
      option => switchDisabledReason(org, option.itemId, option.position, canDelete) !== null,
    ),
  );
  return blocked ? REPLACE_NEEDS_DELETE_REASON : null;
}

function usePresetHeight() {
  const bodyRef = useRef<HTMLDivElement>(null);
  const presetRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const body = bodyRef.current;
    const preset = presetRef.current;
    if (!body || !preset) return;
    const report = () =>
      body.style.setProperty(PRESET_HEIGHT_PROPERTY, `${preset.getBoundingClientRect().height}px`);
    report();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(report);
    observer?.observe(preset);
    return () => {
      observer?.disconnect();
      body.style.removeProperty(PRESET_HEIGHT_PROPERTY);
    };
  }, []);
  return { bodyRef, presetRef };
}

/**
 * Floats over the page: one clashing organisation's items as a table with a row per slot, source
 * content beside target content, with a preset for the whole firm and one choice per row. The panel
 * sits in a clipping shell that grows from the right edge while the panel keeps its full width. The
 * page owns focus and the move: `open` and `moving` say where the shell is, and `titleRef` lets the
 * page focus the title.
 */
export function ComparePanel({
  org,
  control,
  onChange,
  sourceName,
  targetName,
  canDelete,
  loading = false,
  onClose,
  open = true,
  moving = false,
  shellRef,
  titleRef,
}: ComparePanelProps) {
  const name = org.companyName ?? "Organisation";
  const items = org.items;
  const count = items ? changeCount(org, control) : 0;
  const leftOutReason = control.leftOut ? LEFT_OUT_FIRM_REASON : null;
  const presetReason = presetReasonFor(org, canDelete);
  const rows = slotRowsOf(org);
  const reason = leftOutReason ?? presetReason ?? switchReasonFor(org, rows, canDelete);
  const { bodyRef, presetRef } = usePresetHeight();
  const key = firmKey(org);
  const [searchState, setSearchState] = useState({ key, text: "" });
  const searchable = items ? items.source.length + items.target.length > SEARCH_FROM_ITEMS : false;
  const search = searchable && searchState.key === key ? searchState.text : "";
  const setSearch = (text: string) => setSearchState({ key, text });

  function closeOnEscape(event: KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") onClose();
  }

  return (
    <div ref={shellRef} className={styles.shell} data-open={open} data-moving={moving}>
      <aside
        className={styles.panel}
        aria-label={`Compare ${name}`}
        aria-hidden={open ? undefined : true}
        inert={!open}
        onKeyDown={closeOnEscape}
      >
        <div className={styles.head}>
          <button
            type="button"
            className={styles.close}
            aria-label="Close compare panel"
            onClick={onClose}
          >
            <span aria-hidden="true">✕</span>
          </button>
          <div className={styles.micro}>Compare</div>
          <h2 ref={titleRef} tabIndex={-1} className={styles.title}>
            {name}
          </h2>
          <div className={styles.sides} role="group" aria-label="Sides">
            <SideName side="incoming" word="From" name={sourceName} />
            <SideName side="existing" word="Into" name={targetName} />
          </div>
        </div>
        <div ref={bodyRef} className={styles.body}>
          <div ref={presetRef} className={styles.presetBlock}>
            <SegmentedToggle<ClashChoice>
              fill
              name={`preset-${key}`}
              ariaLabel="Preset for every item"
              value={control.choice}
              disabled={leftOutReason !== null}
              options={[
                { value: "keep_target", text: "Keep target's" },
                {
                  value: "take_source",
                  text: "Use source's",
                  disabled: presetReason !== null,
                  title: presetReason ?? undefined,
                },
              ]}
              onChange={choice => onChange(applyPreset(control, choice))}
            />
            {count > 0 && (
              <span className={styles.changes}>
                {count} {count === 1 ? "change" : "changes"}
                <span className={styles.separator} aria-hidden="true">
                  {" · "}
                </span>
                <button
                  type="button"
                  className={styles.reset}
                  onClick={() => onChange(resetFirm(control))}
                >
                  Reset
                </button>
              </span>
            )}
            {count === 0 && (
              <p className={styles.meta}>Sets every item below. Change any one on its own.</p>
            )}
            {reason && <p className={styles.meta}>{reason}</p>}
          </div>
          {loading || !items ? (
            <SkeletonRows rows={SKELETON_ROWS} label="Loading items" />
          ) : (
            <>
              <p className={styles.sentence}>
                <ColouredWords bold text={firmSentence(org, control)} />
              </p>
              {searchable && (
                <div className={styles.search}>
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    onClear={() => setSearch("")}
                    placeholder="Search lawyers..."
                  />
                </div>
              )}
              <ItemTables
                org={org}
                rows={rows}
                control={control}
                canDelete={canDelete}
                leftOutReason={leftOutReason}
                search={search}
                onChange={onChange}
              />
            </>
          )}
        </div>
        <div className={styles.foot}>
          <Button
            variant="outlined"
            color="default"
            className={shared.compactButton}
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </aside>
    </div>
  );
}
